/**
 * Parts owed by the filtered supplier.
 *
 * The part strip answers "what does this part cost and who makes it". A
 * supplier filter spans many parts, so there is no single price or description
 * to put in the strip — this card is that breakdown: every part the supplier
 * still owes, with its description, qty due and unit price.
 *
 * Renders nothing unless a supplier is filtered.
 *
 * @link Forecast for the tab that places it.
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { scheduledDashboardAPI } from '../../../lib/api/scheduled-dashboard';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../ui/table';
import { useFormatters } from '../../../contexts/PreferencesContext';
import type { SupplierPart } from '../../../lib/api/scheduled-dashboard';

interface SupplierPartsCardProps {
  parts: SupplierPart[];
  /** Named in the heading so the card is self-describing. */
  supplierName?: string;
  /** Part count from the supplier tile — used to flag a truncated list. */
  totalParts?: number;
  /** Scope for the export, so the file matches what the card is showing. */
  customerId?: number;
  supplierId?: number;
  erpOrderType?: string;
}

/**
 * @param props - See {@link SupplierPartsCardProps}
 * @returns The card, or null when no supplier is filtered.
 */
export function SupplierPartsCard({
  parts, supplierName, totalParts, customerId, supplierId, erpOrderType,
}: SupplierPartsCardProps) {
  const { formatCurrency, formatDate, formatQuantity } = useFormatters();
  const [exporting, setExporting] = useState(false);

  /**
   * Excel export of this supplier's forward lines. `all` drops the supplier
   * filter and exports the customer's whole pipeline; the filtered form is
   * this card's scope, and carries every part rather than the capped list.
   */
  const handleExport = async (exportAll: boolean) => {
    setExporting(true);
    try {
      const res = await scheduledDashboardAPI.exportForecast({
        customer_id: customerId,
        erp_order_type: erpOrderType,
        ...(exportAll ? { export_all: true } : { supplier_id: supplierId }),
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `parts_due_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(exportAll ? 'Exported all parts due.' : 'Exported parts due.');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Export failed: ${msg}`);
    } finally {
      setExporting(false);
    }
  };

  if (!parts || parts.length === 0) return null;

  // The server caps the list by value; say so rather than quietly showing
  // fewer parts than the tile counts.
  const truncated = typeof totalParts === 'number' && totalParts > parts.length;

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-sm font-medium text-slate-900">
              Parts Due{supplierName ? ` — ${supplierName}` : ''}
            </CardTitle>
            <p className="text-[11px] text-slate-900">
              {truncated
                ? `Top ${parts.length} of ${totalParts} parts by open value — export for the full list`
                : `${parts.length} part${parts.length === 1 ? '' : 's'} with qty still due, highest value first`}
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={exporting}
                className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-medium text-slate-900 hover:bg-slate-100 disabled:opacity-50"
              >
                {exporting
                  ? <Loader2 className="h-3 w-3 animate-spin" />
                  : <Download className="h-3 w-3" />}
                Export
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport(false)}>
                Export these parts
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport(true)}>
                Export all parts
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent>
        <div className="max-h-80 overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-slate-900">Part Number</TableHead>
                <TableHead className="text-slate-900">Supplier Part No</TableHead>
                <TableHead className="text-slate-900">Part Description</TableHead>
                <TableHead className="text-right text-slate-900">Firm Days</TableHead>
                <TableHead className="text-right text-slate-900">Unit Price</TableHead>
                <TableHead className="text-right text-slate-900">Qty Due</TableHead>
                <TableHead className="text-right text-slate-900">Value Due</TableHead>
                <TableHead className="text-slate-900">Next Due</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {parts.map(p => (
                <TableRow key={p.part_number}>
                  <TableCell className="font-mono text-xs font-semibold text-slate-900">
                    {p.part_number}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-900">
                    {p.supplier_part_number || <span className="font-sans text-slate-400">—</span>}
                  </TableCell>
                  <TableCell className="max-w-[320px] truncate text-xs text-slate-900" title={p.part_description}>
                    {p.part_description ? p.part_description.trim() : '—'}
                  </TableCell>
                  {/* No entry = no firm window, so everything future is
                      forecast. Called out rather than shown as a bare dash. */}
                  <TableCell className="text-right text-xs tabular-nums text-slate-900">
                    {p.firm_days !== null
                      ? p.firm_days
                      : <span className="text-slate-500" title="No Part Schedule entry — no firm window, so all future deliveries count as forecast">none</span>}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums text-slate-900">
                    {p.unit_price !== null
                      ? formatCurrency(p.unit_price, p.currency ?? undefined, 2)
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums text-slate-900">
                    {formatQuantity(p.open_qty)}
                  </TableCell>
                  {/* Value Due is qty × unit_price in the PO's OWN currency —
                      no conversion happens anywhere in this pipeline. Label it
                      with that currency; formatting it in the viewer's default
                      would stamp a EUR figure with a £ sign. */}
                  <TableCell className="text-right text-xs font-semibold tabular-nums text-slate-900">
                    {formatCurrency(p.open_value, p.currency ?? undefined)}
                  </TableCell>
                  <TableCell className="text-xs text-slate-900">
                    {p.next_due ? formatDate(p.next_due) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
